import {Box, IconButton} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ErrorWithContext, MultipleLineError, PendingWrapper} from "@dvrt/graphql-widgets";
import {removeFileExtension} from "@/tools";
import DomainMediaSelector, {DomainMediaSelectorOnChange} from "@/components/DomainMediaSelector";
import {DomainAudioInput} from "@/api";
import DialogMediaPlayer from "@/components/DialogMediaPlayer";
import useDialogManager from "@/hooks/useDialogManager";
import {useMediaDownloadTokenQuery} from "@/queries.generated";
import ThemedFontAwesomeIcon from "@/components/ThemedFontAwesomeIcon";
import {FC, useCallback, useEffect} from "react";

type Props = {
    audio: DomainAudioInput;
    disabled: boolean;
    onChange: (audio: DomainAudioInput | null) => void;
    domainId: number;
    errors?: ErrorWithContext[];
};

export const AudioRow = ({audio, disabled, onChange, domainId, errors}: Props) => {
    const playDialogManager = useDialogManager<{token: string | undefined}>({token: undefined});
    const canPlayAudio = !!audio && !audio.file && !!audio.id && !audio?.unset;

    const {data: downloadTokenData, loading: downloadTokenLoading} = useMediaDownloadTokenQuery({
        variables: {id: audio?.id},
        skip: !canPlayAudio || !playDialogManager.isOpen,
    });

    useEffect(() => {
        if (playDialogManager.isOpen) {
            playDialogManager.setData({token: downloadTokenData?.media.items[0].downloadToken});
        }
        // if we include the whole playDialogPlayer in the dependency array, it would cause
        // an infinite rerender, as it would change its own data via .setData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [downloadTokenData?.media.items, playDialogManager.isOpen, playDialogManager.setData]);

    const handleSelectAudio: DomainMediaSelectorOnChange = useCallback(
        (value) => {
            if (value instanceof File) {
                onChange({
                    id: undefined,
                    description: removeFileExtension(value.name),
                    file: value,
                    unset: false,
                });
            } else if (value) {
                onChange({
                    id: value.id,
                    description: value.description,
                    file: undefined,
                    unset: false,
                });
            }
        },
        [onChange],
    );

    const handleRemoveFile = useCallback(
        () =>
            onChange({
                id: undefined,
                description: undefined,
                file: undefined,
                unset: true,
            }),
        [onChange],
    );

    return (
        <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
                <DomainMediaSelector
                    domainId={domainId}
                    value={audio?.id ?? audio?.file}
                    onChange={handleSelectAudio}
                />
                <MultipleLineError errors={errors} />
            </Box>
            <Box display="flex">
                {canPlayAudio && (
                    <PendingWrapper pending={downloadTokenLoading}>
                        <IconButton onClick={playDialogManager.open}>
                            <ThemedFontAwesomeIcon icon="play" />
                        </IconButton>
                    </PendingWrapper>
                )}
                {audio && !audio.unset && <DeleteButton disabled={disabled} onClick={() => handleRemoveFile()} />}
            </Box>
            <DialogMediaPlayer
                open={playDialogManager.isOpen}
                onClose={playDialogManager.close}
                mediaDownloadToken={playDialogManager.data.token}
            />
        </Box>
    );
};

const DeleteButton: FC<{disabled: boolean; onClick: () => void}> = ({disabled, onClick}) => (
    <IconButton color="secondary" disabled={disabled} onClick={onClick} size="large">
        <FontAwesomeIcon icon="times" />
    </IconButton>
);
